import React, { useRef, useState } from "react";
import { Loader2 } from "lucide-react";

const IMAGE_TYPES = [
  "editorial illustration", "realistic photograph", "chapter opener", "product mockup",
  "icon-based graphic", "decorative divider", "infographic", "diagram", "timeline",
  "decision tree", "process flow", "comparison", "roadmap", "cycle", "hierarchy", "checklist graphic",
];

const DIAGRAM_TYPES = ["roadmap", "process flow", "timeline", "hierarchy", "cycle", "comparison"];

export default function ImagesAssistantPanel({
  selectedBlock,
  chapterTitle,
  busy,
  form,
  onFormChange,
  onGenerateChapterImage,
  onGenerateSectionImage,
  onGenerateDiagram,
  onUpload,
  onUseReference,
  onGenerateVariations,
  onReplaceSelected,
  onRemoveSelected,
  onSaveToLibrary,
  onConvertPlaceholders,
  mediaLibrary = [],
  onInsertFromLibrary,
  error,
}) {
  const fileRef = useRef(null);
  const refFileRef = useRef(null);
  const [localNote, setLocalNote] = useState("");
  const isImage = selectedBlock?.type === "image";
  const isDiagram = selectedBlock?.type === "diagram";
  const set = (k, v) => onFormChange({ ...form, [k]: v });

  return (
    <section className="bww-right-section" data-testid="bl-images-panel">
      <h3>Chapter visuals</h3>
      <p>
        {selectedBlock
          ? `Selected: ${selectedBlock.type.replace(/_/g, " ")}`
          : `Working on: ${chapterTitle || "chapter"}`}
      </p>
      {error && <div className="bww-panel-error" data-testid="bl-images-error">{error}</div>}
      {localNote && <div className="bww-panel-note">{localNote}</div>}

      <div className="bww-style-grid">
        <label className="bww-style-field">
          <span>Prompt</span>
          <textarea
            data-testid="bl-image-prompt"
            rows={3}
            value={form.prompt || ""}
            onChange={(e) => set("prompt", e.target.value)}
            placeholder="Describe the image or diagram…"
          />
        </label>
        <label className="bww-style-field">
          <span>Image type</span>
          <select data-testid="bl-image-type" value={form.imageType || "editorial illustration"} onChange={(e) => set("imageType", e.target.value)}>
            {IMAGE_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </label>
        <label className="bww-style-field">
          <span>Visual style</span>
          <input data-testid="bl-image-style" value={form.visualStyle || ""} onChange={(e) => set("visualStyle", e.target.value)} placeholder="e.g. editorial, flat, documentary" />
        </label>
        <label className="bww-style-field">
          <span>Orientation</span>
          <select value={form.orientation || "landscape"} onChange={(e) => set("orientation", e.target.value)}>
            <option value="square">Square</option>
            <option value="portrait">Portrait</option>
            <option value="landscape">Landscape</option>
          </select>
        </label>
        <label className="bww-style-field">
          <span>Placement</span>
          <select data-testid="bl-image-placement" value={form.placement || "inline"} onChange={(e) => set("placement", e.target.value)}>
            <option value="full_width">Full width</option>
            <option value="half_width">Half width</option>
            <option value="inline">Inline</option>
          </select>
        </label>
        <label className="bww-style-field">
          <span>Caption</span>
          <input data-testid="bl-image-caption" value={form.caption || ""} onChange={(e) => set("caption", e.target.value)} />
        </label>
        <label className="bww-style-field">
          <span>Alt text</span>
          <input data-testid="bl-image-alt" value={form.alt || ""} onChange={(e) => set("alt", e.target.value)} />
        </label>
      </div>

      <div className="bww-action-list" style={{ marginTop: 12 }}>
        <button type="button" className="bww-assistant-action" data-testid="bl-generate-chapter-image"
          disabled={busy} onClick={onGenerateChapterImage}>
          {busy === "gen-chapter" ? <><Loader2 className="animate-spin" size={12} /> Generating…</> : "Generate chapter image"}
        </button>
        <button type="button" className="bww-assistant-action" data-testid="bl-generate-section-image"
          disabled={busy || !selectedBlock} title={!selectedBlock ? "Select a block first" : undefined}
          onClick={onGenerateSectionImage}>
          {busy === "gen-section" ? "Generating…" : "Generate image for selected section"}
        </button>
        <button type="button" className="bww-assistant-action" data-testid="bl-generate-diagram"
          disabled={busy} onClick={() => {
            if (DIAGRAM_TYPES.includes(form.imageType) || form.imageType === "diagram" || form.imageType === "roadmap") {
              onGenerateDiagram();
            } else {
              setLocalNote("Diagrams use native SVG — labels stay editable. Switching to roadmap diagram.");
              onFormChange({ ...form, imageType: "roadmap" });
              onGenerateDiagram();
            }
          }}>
          Generate diagram
        </button>
        <button type="button" className="bww-assistant-action" data-testid="bl-upload-image"
          disabled={busy} onClick={() => fileRef.current?.click()}>
          {busy === "upload" ? "Uploading…" : "Upload image"}
        </button>
        <input ref={fileRef} type="file" accept="image/*" hidden data-testid="bl-upload-input"
          onChange={(e) => { const f = e.target.files?.[0]; if (f) onUpload(f); e.target.value = ""; }} />
        <button type="button" className="bww-assistant-action" data-testid="bl-use-reference"
          disabled={busy} onClick={() => refFileRef.current?.click()}>
          Use reference image
        </button>
        <input ref={refFileRef} type="file" accept="image/*" hidden
          onChange={(e) => { const f = e.target.files?.[0]; if (f) onUseReference(f); e.target.value = ""; }} />
        <button type="button" className="bww-assistant-action" data-testid="bl-generate-variations"
          disabled={busy || !isImage || !selectedBlock?.content?.url}
          title={!isImage ? "Select an image block with a generated asset" : undefined}
          onClick={onGenerateVariations}>
          {busy === "variations" ? "Generating variations…" : "Generate variations"}
        </button>
        <button type="button" className="bww-assistant-action" data-testid="bl-replace-image"
          disabled={busy || (!isImage && !isDiagram)} onClick={onReplaceSelected}>
          Replace selected image
        </button>
        <button type="button" className="bww-assistant-action" data-testid="bl-remove-image"
          disabled={busy || (!isImage && !isDiagram)} onClick={onRemoveSelected}>
          Remove selected image
        </button>
        <button type="button" className="bww-assistant-action" data-testid="bl-save-media-library"
          disabled={busy || !isImage || !selectedBlock?.content?.assetId} onClick={onSaveToLibrary}>
          Save to book media library
        </button>
        <button type="button" className="bww-assistant-action" data-testid="bl-convert-placeholders"
          disabled={busy} onClick={onConvertPlaceholders}>
          Convert [DIAGRAM]/[DOODLE] placeholders
        </button>
      </div>

      {mediaLibrary.length > 0 && (
        <div className="bww-media-library" data-testid="bl-media-library">
          <h4>Book media library</h4>
          {mediaLibrary.map((item) => (
            <button key={item.id} type="button" className="bww-assistant-action"
              onClick={() => onInsertFromLibrary(item)}>
              Insert: {item.prompt?.slice(0, 48) || item.id}
            </button>
          ))}
        </div>
      )}
    </section>
  );
}
